import { ActionButton, FormInputRow, Input } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Box, Button, Collapse, Divider, Grid, List, ListItem, ListItemText, Switch, TextField, Typography, useTheme } from '@mui/material'
import { KeyboardArrowRight } from '@mui/icons-material'
import React, { useState, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useFeatureFormContext } from './feature-form-context'
import { useStyles } from './style'
import { useDispatch } from 'react-redux'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'

const CornerstoneSettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { tenant } = useFeatureFormContext()
  const form = useForm({ mode: 'onChange' })
  const { getValues, setValue, trigger, control, formState: { errors, isValid } } = form

  const [isLoading, setIsLoading] = useState(false)
  const [checked, setChecked] = useState(!!tenant.features?.cornerstoneIntegration?.enabled)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const handleSwitch = () => setChecked(checked => {
    if (checked) setIsCollapsed(false)
    return !checked
  })
  const handleClickListItem = () => setIsCollapsed(isCollapsed => !isCollapsed)

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdateCornerstoneIntegration',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'Cornerstone configuration updated' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: () => {
        BannerStatic.show({ label: 'There was an error saving the Cornerstone configuration', isError: true })
        setIsLoading(false)
      }
    }
  ])

  const [testConnection] = useEvent([
    {
      eventName: 'cornerstoneTestConnection',
      onSuccess: (r) => {
        let label
        if (r?.ok) {
          label = r.listing
            ? `Connected. Drop folder has ${r.listing.length} entries.`
            : 'Connected — login OK.'
          if (r.folderWarning) label += ` ${r.folderWarning}`
        } else {
          label = `Connection failed: ${r?.error || 'unknown error'}`
        }
        BannerStatic.show({ label, isError: !r?.ok })
        setIsLoading(false)
      },
      onError: () => {
        BannerStatic.show({ label: 'There was an error testing the connection', isError: true })
        setIsLoading(false)
      }
    }
  ])

  const [runFeed] = useEvent([
    {
      eventName: 'cornerstoneRunNow',
      onSuccess: (r) => {
        BannerStatic.show({ label: r?.dryRun ? 'Dry run started — artefacts will be staged to S3 (no SFTP).' : 'Feed run started.' })
        setIsLoading(false)
      },
      onError: () => {
        BannerStatic.show({ label: 'There was an error starting the run', isError: true })
        setIsLoading(false)
      }
    }
  ])

  // SFTP password is kept OUT of the form (getValues) so it never rides on the
  // config-save payload — it goes straight to SSM via its own event.
  const [sftpPassword, setSftpPassword] = useState('')
  const [setPassword] = useEvent([
    {
      eventName: 'cornerstoneSetSftpPassword',
      onSuccess: () => {
        BannerStatic.show({ label: 'SFTP password stored securely.' })
        setSftpPassword('')
        setIsLoading(false)
      },
      onError: () => {
        BannerStatic.show({ label: 'There was an error storing the SFTP password', isError: true })
        setIsLoading(false)
      }
    }
  ])

  const handleSave = () => {
    const values = getValues()
    tenantUpdate.trigger({ pk: tenant.pk, config: { ...values, sftpPort: Number(values.sftpPort), enabled: checked } })
    setIsLoading(true)
  }
  const handleTest = () => { testConnection.trigger({ tenantPk: tenant.pk }); setIsLoading(true) }
  const handleRun = () => { runFeed.trigger({ tenantPk: tenant.pk }); setIsLoading(true) }
  const handleDryRun = () => { runFeed.trigger({ tenantPk: tenant.pk, dryRun: true }); setIsLoading(true) }
  const handleSetPassword = () => { setPassword.trigger({ tenantPk: tenant.pk, password: sftpPassword }); setIsLoading(true) }

  const isSaveDisabled = useMemo(() => isLoading || (!isValid && checked), [isLoading, isValid, checked])
  const areActionsDisabled = useMemo(() => isLoading || !checked || !isValid, [isLoading, isValid, checked])

  // Load existing config into the form.
  useEffect(() => {
    const cfg = tenant.features?.cornerstoneIntegration
    if (cfg) {
      Object.entries(cfg).forEach(([name, value]) => setValue(name, value))
      trigger()
    }
  }, [tenant.features, setValue, trigger])

  const field = (name, label, description, defaultValue, required) => (
    <Grid item xs={12}>
      <FormInputRow
        title={label}
        description={description}
        smallFormTitles
        InputComponent={
          <Input
            label={label}
            name={name}
            control={control}
            defaultValue={defaultValue}
            rules={required ? { required: true } : undefined}
            valid={errors[name] === undefined}
            error={`${label} is required`}
            showErrors={!!required}
          />
        }
      />
    </Grid>
  )

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box border={1} borderColor='#E0E0E0' style={{ padding: spacing(1, 4) }}>
          <Box display='flex' style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant='body2' style={{ marginTop: spacing(3.5) }}>
              Cornerstone (CSOD) SFTP feed
            </Typography>
            <Box display='flex' style={{ justifyContent: 'flex-end', alignItems: 'flex-start', padding: spacing(1) }}>
              <Box visibility={checked ? 'visible' : 'hidden'}>
                <List component='nav' aria-label='Cornerstone configuration'>
                  <ListItem button aria-haspopup='true' onClick={handleClickListItem}>
                    <ListItemText disableTypography style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant='body2'>Configure feed</Typography>
                      <KeyboardArrowRight />
                    </ListItemText>
                  </ListItem>
                </List>
              </Box>
              {checked && (
                <Divider orientation='vertical' flexItem style={{ margin: spacing(3, 0), borderColor: 'rgba(0, 0, 0, 0.38)' }} />
              )}
              <Box style={{ padding: spacing(2, 0.5) }}>
                <Switch color='primary' checked={checked} onChange={handleSwitch} disabled={isLoading} />
              </Box>
            </Box>
          </Box>

          <Collapse in={isCollapsed}>
            <Box className={classes.formCtn}>
              {field('vendorProvider', 'Vendor / Provider', 'CSOD provider name. Must already exist in the portal.', 'Mindtools Kineo', true)}
              {field('businessAreaOwner', 'Business Area / SME Owner', 'CSOD Business Area / SME Owner (column AM).', 'Kathryn Wormald', true)}
              {field('sftpHost', 'SFTP host', 'e.g. ftp.sainsburys-pilot.csod.com (pilot) or ftp.sainsburys.csod.com (prod).')}
              {field('sftpPort', 'SFTP port', 'Defaults to 22.', '22')}
              {field('sftpUser', 'SFTP username', 'CSOD SFTP username.')}
              <Grid item xs={12}>
                <FormInputRow
                  title='SFTP password'
                  description='Stored as an SSM SecureString at /cornerstone/sftp/<tenantPk>. Write-only: never shown back, never saved on the tenant. Leave blank to keep the current one.'
                  smallFormTitles
                  InputComponent={
                    <Box display='flex' style={{ gap: spacing(1), alignItems: 'center' }}>
                      <TextField
                        type='password'
                        size='small'
                        fullWidth
                        autoComplete='new-password'
                        placeholder='Paste the CSOD SFTP password'
                        value={sftpPassword}
                        onChange={(e) => setSftpPassword(e.target.value)}
                      />
                      <Button variant='outlined' disabled={isLoading || !sftpPassword} onClick={handleSetPassword}>
                        Set password
                      </Button>
                    </Box>
                  }
                />
              </Grid>
              {field('dropFolder', 'CSV drop folder', 'e.g. /clients/sainsburys/content/mindtools')}
              {field('thumbnailFolder', 'Thumbnail drop folder', 'e.g. /clients/sainsburys/LMS/materials/thumbnail (bmp/gif/jpg/jpeg/png, max 120 kB, 2:1).')}
              {field('thumbnailFileExt', 'Thumbnail file extension', 'Defaults to jpg.', 'jpg')}
              {field('fileNameTemplate', 'CSV file name template', "Must start with 'mindtools'. Tokens: {YYYYMMDD}, {tenantPk}, {runId}.", 'mindtools_sainsburys_{YYYYMMDD}.csv')}
              {field('defaultLanguage', 'Default language', 'Defaults to en-GB.', 'en-GB')}
              {field('defaultSubjectId', 'Default Subject ID', 'Optional fallback when no category matches.')}
              {field('mobileDefault', 'Mobile default', 'Defaults to Yes.', 'Yes')}
              {field('availabilityOu', 'Availability to OU', "CSOD 'Availability to OU' group name. Differs per environment: Prod = LXP_Content_Avail, Pilot = LXP-test. Defaults to LXP_Content_Avail.", 'LXP_Content_Avail')}
            </Box>
          </Collapse>
          <Divider style={{ marginBottom: spacing(1) }} />

          <Box className={classes.ctnControls} display='flex' justifyContent='flex-end'>
            {checked && (
              <>
                <ActionButton onClick={handleTest} disabled={areActionsDisabled}>Test connection</ActionButton>
                <ActionButton onClick={handleDryRun} disabled={isSaveDisabled}>Dry run (S3 only)</ActionButton>
                <ActionButton onClick={handleRun} disabled={areActionsDisabled}>Run now</ActionButton>
              </>
            )}
            <ActionButton onClick={handleSave} disabled={isSaveDisabled} variant='primary'>Save</ActionButton>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export default CornerstoneSettings
